import React, { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { Col, Form, Row } from "react-bootstrap";
import Select from "react-select";
import { useFetchCategoryFieldSets } from "hooks/useCategoryFieldSet";

const Specifications = () => {
  const { setValue, watch } = useFormContext();

  const categoryId = watch("categoryId");
  const specs = watch("specifications") || [];

  const { data: categoryFieldSetData, isLoading } = useFetchCategoryFieldSets();

  /* --------------------------------------------
     GET CATEGORY FIELDSETS
  ---------------------------------------------*/
  const categoryFieldSets = useMemo(() => {
    if (!categoryFieldSetData || !categoryId) return [];

    const category = categoryFieldSetData.data.data.find(
      (c) => String(c.categoryId) === String(categoryId),
    );

    return category?.fieldSets || [];
  }, [categoryFieldSetData, categoryId]);

  /* --------------------------------------------
     INIT SPECS FOR ALL FIELDSETS
  ---------------------------------------------*/
  useEffect(() => {
    if (!categoryFieldSets.length) return;

    const newSpecs = [];

    categoryFieldSets.forEach((fs) => {
      fs.fields.forEach((field) => {
        const exists = specs.find(
          (s) =>
            String(s.fieldSetId) === String(fs.id) &&
            String(s.fieldId) === String(field.id),
        );

        if (!exists) {
          newSpecs.push({
            fieldSetId: fs.id,
            fieldId: field.id,
            fieldValue: "",
            status: false,
            group: field.group || null,
          });
        }
      });
    });

    if (newSpecs.length) {
      setValue("specifications", [...specs, ...newSpecs]);
    }
  }, [categoryFieldSets]);

  /* --------------------------------------------
     HANDLE INPUT CHANGE
  ---------------------------------------------*/
  const handleInputChange = (fieldSetId, fieldId, fieldType, value, group) => {
    const updated = specs.map((item) => {
      if (fieldType === "radio" && group) {
        // Reset all radio fields in the same group within the same fieldSet
        const isSameGroup =
          String(item.group || "") === String(group) &&
          String(item.fieldSetId) === String(fieldSetId);
        if (isSameGroup) {
          return {
            ...item,
            fieldValue: item.fieldId === fieldId ? value : "",
          };
        }
      }

      if (
        String(item.fieldSetId) === String(fieldSetId) &&
        String(item.fieldId) === String(fieldId)
      ) {
        const fieldValue =
          fieldType === "multiSelect"
            ? value?.map((v) => v.value).join("|")
            : value;
        return { ...item, fieldValue };
      }

      return item;
    });

    setValue("specifications", updated, { shouldDirty: true });
  };

  if (isLoading) return <div>Loading...</div>;

  /* --------------------------------------------
     RENDER
  ---------------------------------------------*/
  return (
    <>
      {categoryFieldSets
        ?.filter((d) => d.purpose === "Specification")
        ?.filter((d) => d.status)
        .map((fieldSet) => {
          // Group radio fields by group
          const radioGroups = {};
          fieldSet.fields.forEach((field) => {
            if (field.type === "radio") {
              const groupKey = field.group || fieldSet.id;
              if (!radioGroups[groupKey]) radioGroups[groupKey] = [];
              radioGroups[groupKey].push(field);
            }
          });

          return (
            <Row className="gx-1 mb-3" key={fieldSet.id}>
              {fieldSet.fields
                ?.filter((f) => f.type !== "hidden" && f.type !== "radio")
                .map((field) => {
                  const currentInput = specs.find(
                    (s) =>
                      String(s.fieldSetId) === String(fieldSet.id) &&
                      String(s.fieldId) === String(field.id),
                  );

                  const value = currentInput?.fieldValue || "";

                  const selectedValues = value
                    ? value
                        .split("|")
                        .map((val) => ({ value: val, label: val }))
                    : [];

                  const inputProps = {
                    placeholder: field.placeholder,
                    max: field.max,
                    maxLength: field.max,
                    min: field.min,
                    // required: field.isRequired,
                    readOnly: field.isReadOnly,
                  };

                  // console.log(inputProps);

                  return (
                    <Col md="4" key={field.id}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fs-9">{field.label}</Form.Label>

                        {/* TEXT INPUTS */}
                        {[
                          "text",
                          "password",
                          "date",
                          "color",
                          "email",
                          "number",
                          "time",
                          "datetime-local",
                        ].includes(field.type) && (
                          <Form.Control
                            type={field.type}
                            placeholder={field.placeholder}
                            value={value}
                            {...inputProps}
                            onChange={(e) =>
                              handleInputChange(
                                fieldSet.id,
                                field.id,
                                field.type,
                                e.target.value,
                              )
                            }
                          />
                        )}

                        {/* SELECT */}
                        {field.type === "select" && (
                          <Form.Select
                            value={value}
                            {...inputProps}
                            onChange={(e) =>
                              handleInputChange(
                                fieldSet.id,
                                field.id,
                                "select",
                                e.target.value,
                              )
                            }
                          >
                            <option value="">Select</option>
                            {field.listValues?.split("|").map((opt, i) => (
                              <option key={i} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </Form.Select>
                        )}

                        {/* MULTI SELECT */}
                        {field.type === "multiSelect" && (
                          <Select
                            isMulti
                            closeMenuOnSelect={false}
                            value={selectedValues}
                            options={field.listValues?.split("|").map((v) => ({
                              value: v,
                              label: v,
                            }))}
                            onChange={(opts) =>
                              handleInputChange(
                                fieldSet.id,
                                field.id,
                                "multiSelect",
                                opts,
                              )
                            }
                          />
                        )}

                        {/* CHECKBOX */}
                        {field.type === "checkbox" && (
                          <Form.Check
                            type="checkbox"
                            label={field.label}
                            checked={value === "true"}
                            onChange={(e) =>
                              handleInputChange(
                                fieldSet.id,
                                field.id,
                                "checkbox",
                                e.target.checked ? "true" : "false",
                              )
                            }
                          />
                        )}

                        {/* TEXTAREA */}
                        {field.type === "textarea" && (
                          <Form.Control
                            as="textarea"
                            value={value}
                            {...inputProps}
                            placeholder={field.placeholder}
                            onChange={(e) =>
                              handleInputChange(
                                fieldSet.id,
                                field.id,
                                "textarea",
                                e.target.value,
                              )
                            }
                          />
                        )}
                      </Form.Group>
                    </Col>
                  );
                })}

              {/* RENDER RADIO GROUPS */}
              {Object.keys(radioGroups).map((groupKey) => {
                const fields = radioGroups[groupKey];

                // console.log("specs", specs);

                // Get the current value for this group from specs
                const value = specs.find(
                  (s) => s.group === groupKey,
                )?.fieldValue;

                // console.log(value);
                return (
                  <Col md="12" key={groupKey}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fs-9">
                        {fields[0].label || "Options"}
                      </Form.Label>
                      <div className="d-flex gap-3 flex-wrap">
                        {fields.map((field, i) => (
                          <Form.Check
                            key={`${field.id}-${i}`}
                            type="radio"
                            name={`radio-group-${groupKey}`}
                            value={field.name} // store field name
                            label={field.name} // show label if exists
                            // checked={value === field.name} // compare with stored value
                            onChange={() =>
                              handleInputChange(
                                field.fieldSetId,
                                field.id,
                                "radio",
                                field.name, // store field name
                                groupKey,
                              )
                            }
                          />
                        ))}
                      </div>
                    </Form.Group>
                  </Col>
                );
              })}
            </Row>
          );
        })}
    </>
  );
};

export default Specifications;
